import { Order } from '../../types/orders';
import { CheckoutConfig } from '../../types/checkout';
import { ExportToCsv } from 'export-to-csv';
import { formatDate, formatTime, toCommonCurrencyAmount } from '../../helpers/formatters';
import { getLocaleNumeralDecimal } from '../../helpers/intl';
import { downloadDataTablePdf } from '../../helpers/download/dataTablePdf';

export enum ActionTypes {
    GetOrders = '[orders] GET_ORDERS',
    FetchOrdersResponse = '[orders] FETCH_ORDERS_RESPONSE',
    FetchNextResponse = '[orders] FETCH_NEXT_RESPONSE',
    SwapOrder = '[orders] SWAP_ORDER',
    GetCheckoutConfiguration = '[orders] GET_CHECKOUT_CONFIGURATION',
    FetchCheckoutConfigurationResponse = '[orders] CHECKOUT_CONFIGURATION_RESPONSE',
}

export enum GetOrdersReason {
    mount = 'mount',
    update = 'update',
    next = 'next',
    refresh = 'refresh',
}

export interface GetOrders {
    type: ActionTypes.GetOrders;
    payload: {
        accountId: string;
        params: URLSearchParams;
        reason: GetOrdersReason;
        requestId: number;
    };
}

export interface FetchOrdersResponse {
    type: ActionTypes.FetchOrdersResponse;
    payload: {
        orders: Order[];
    };
    meta: {
        cause: GetOrders;
    };
}

export interface FetchNextResponse {
    type: ActionTypes.FetchNextResponse;
    payload: {
        orders: Order[];
    };
    meta: {
        cause: FetchOrdersResponse;
    };
}

export function getOrders(accountId: string, params: URLSearchParams, origin: GetOrdersReason): GetOrders {
    return {
        type: ActionTypes.GetOrders,
        payload: {
            accountId,
            params,
            reason: origin,
            requestId: performance.now(),
        },
    };
}

export interface SwapOrder {
    type: ActionTypes.SwapOrder;
    payload: {
        order: Order;
    };
    meta: {
        cause: FetchOrdersResponse;
    };
}

const mapOrderLine = (order: Order, t: (o: string) => string, language: string) => ({
    // TODO expand as needed
    [t('payments.fields.date')]: formatDate(order.created_at, language),
    [t('payments.fields.time')]: formatTime(order.created_at, language),
    [t('payments.fields.currency')]: order.currency,
    [t('payments.fields.amount')]: order.net_amount ? toCommonCurrencyAmount(order.net_amount, order.currency) : '',
    [t('orders.fields.customer.header')]: (order.customer && order.customer.id) || '',
    [t('orders.fields.status')]: t(`orders.order.status.${order.status}`),
    'Dintero ID': order.id,
});

export function createOrdersCsv(aid: string, orders: Order[], t: (o: string) => string, language: string): void {
    if (orders.length === 0) {
        return;
    }
    const data = orders.map((order) => mapOrderLine(order, t, language));
    const now = new Date().toISOString();
    const nowFormatted = `${formatDate(now, language)}T${formatTime(now, language)}`;
    const options = {
        fieldSeparator: ';',
        quoteStrings: '"',
        decimalSeparator: getLocaleNumeralDecimal(language),
        showLabels: true,
        showTitle: true,
        title: `Dintero orders download ${aid}, ${nowFormatted}`,
        filename: `${aid}_${nowFormatted.replace(':', '-')}_Dintero-orders`,
        useTextFile: false,
        useBom: true,
        useKeysAsHeaders: true,
    };
    const csvExporter = new ExportToCsv(options);
    csvExporter.generateCsv(data);
}

export function createOrdersPdf(aid: string, orders: Order[], t: (o: string) => string, language: string): void {
    if (orders.length === 0) {
        return;
    }
    const data = orders.map((order) => mapOrderLine(order, t, language));
    const now = new Date().toISOString();
    const nowFormatted = `${formatDate(now, language)}T${formatTime(now, language)}`;
    downloadDataTablePdf(
        aid,
        `Dintero orders download ${aid}, ${nowFormatted}`,
        nowFormatted,
        data,
        language,
        `${aid}_${nowFormatted.replace(':', '-')}_Dintero-orders`
    );
}

export interface GetCheckoutConfiguration {
    type: ActionTypes.GetCheckoutConfiguration;
    payload: {
        accountIds: string[];
    };
}

export function getCheckoutConfiguration(accountIds: string[]): GetCheckoutConfiguration {
    return {
        type: ActionTypes.GetCheckoutConfiguration,
        payload: {
            accountIds: accountIds,
        },
    };
}

export interface FetchCheckoutConfigurationResponse {
    type: ActionTypes.FetchCheckoutConfigurationResponse;
    payload: {
        configurations: { [accountId: string]: CheckoutConfig };
    };
}

export type Actions =
    | GetOrders
    | FetchOrdersResponse
    | FetchNextResponse
    | SwapOrder
    | GetCheckoutConfiguration
    | FetchCheckoutConfigurationResponse;
